import React from 'react';
import {useTranslation} from 'react-i18next';
import {
  LayoutGrid,
  UserIcon,
  KeyRoundIcon,
  CreditCard,
  LogOut,
  Star,
  MessageCircleIcon,
  BookOpen,
  Compass,
  Luggage,
  Smartphone,
} from 'lucide-react';
import {useLuggageBookings} from './useLuggageBookings';
import {useFetchEsims} from './esim/useFetchEsims';
import {NavLinks} from './useNavigationLinks';
import {PATHS} from 'routes/constants';
import {useSummary} from 'context/summary';
import {usePaymentsCart, useBuildTotalPayments} from './payments';
import {usePaymentSettings} from './usePaymentSettings';
import {usePath} from './common';
import {useLocation, useMatch} from 'react-router-dom';
import {useGuardedNavigate} from 'context/navigationGuard';
import {useNavigationCustom} from './useNavigationCustom';
import {useRemoteAccessEnabled} from './remoteAccess';
import {useFetchGuidebooks} from './guidebooks/useFetchGuidebooks';
import {useHomeGuidebooksPath} from './guidebooks/useHomeGuidebooksPath';
import {useGuestGroup} from './useGuestGroup';
import {useBuildNavigationItems} from './useBuildNavigationItems';
import {useCheckoutSettings} from 'pages/Checkout/hooks/useCheckoutSettings';
import {useAnalytics, POSTHOG_EVENTS} from 'analytics';
import {useChatSettings} from './useChatSettings';
import {isTravelGuideEnabled} from 'utils/travelGuide';

const GUIDEBOOK_DETAIL_PATH = new RegExp(
  `/(${[PATHS.guidebook, PATHS.guidebookHtml, PATHS.guidebookPdf].join('|')})(/|$)`,
);

type SideBarLinks = {
  [key: string]: {
    label: NavLinks;
    selected: boolean;
    disabled: boolean;
    icon: JSX.Element;
    onClick: () => void;
    extraData?: string;
    extraDataVariant?: 'default' | 'warning' | 'teal';
  };
};

function useSideBarLinks() {
  const {t} = useTranslation();
  const {isChatEnabled} = useChatSettings();
  const {arePaymentsAvailable, data, reservationId} = useSummary();
  const navigate = useGuardedNavigate();
  const {pathname} = useLocation();
  const {getFullPath, getMainPath} = usePath();
  const homeGuidebooksPath = useHomeGuidebooksPath();
  const {currentPage} = useNavigationCustom();
  const {track} = useAnalytics();
  const {isCheckoutEnabled, isAvailable} = useCheckoutSettings();
  const {totalIncompletedGuests, isRegistrationComplete} = useGuestGroup();

  const {getPaymentPath, getRegistrationPath} = useBuildNavigationItems();
  const isRemoteAccessEnabled = useRemoteAccessEnabled();
  const {paymentsCart} = usePaymentsCart({refetchOnMount: false});
  const {currencySymbol} = usePaymentSettings();
  const {totalToPayNow} = useBuildTotalPayments({paymentsCart});
  const {data: guidebooksData} = useFetchGuidebooks({reservationId});
  const hasGuidebooks = (guidebooksData?.items?.length ?? 0) > 0;
  const isHomeView = useMatch(getMainPath(PATHS.home));
  const isGuidebooksView = GUIDEBOOK_DETAIL_PATH.test(pathname);
  const isPaymentsView =
    pathname.includes(PATHS.payments.main) ||
    pathname.includes(PATHS.deposits) ||
    pathname.includes(PATHS.taxes.main);
  const isRemoteAccessView = useMatch(getMainPath(PATHS.remoteAccess));
  const isCheckOutView = useMatch(getMainPath(PATHS.checkout.main));
  const isRecommendationsView = pathname.includes(PATHS.recommendations.main);
  const isChatView = useMatch(getMainPath(PATHS.chat));
  const isTravelGuideView = useMatch(getMainPath(PATHS.travelGuide));
  const isLuggageView = useMatch(getMainPath(PATHS.luggage));
  const {hasBookings: hasLuggageBookings, activeBookings: activeLuggageBookings} =
    useLuggageBookings();
  const isEsimView = useMatch(getMainPath(PATHS.esim.my));
  const {data: esims} = useFetchEsims({reservation_id: reservationId || ''});
  const hasEsims = Boolean(esims?.length);

  const mainLinks = React.useMemo(() => {
    const links: SideBarLinks = {
      [NavLinks.home]: {
        label: NavLinks.home,
        selected: Boolean(isHomeView),
        disabled: false,
        icon: <LayoutGrid />,
        onClick: () => navigate(getFullPath(PATHS.home)),
      },
      [NavLinks.onlineChekin]: {
        label: NavLinks.onlineChekin,
        selected:
          !isHomeView &&
          !isPaymentsView &&
          !isRemoteAccessView &&
          !isCheckOutView &&
          !isRecommendationsView &&
          !(isChatEnabled && isChatView) &&
          !isLuggageView &&
          !isGuidebooksView &&
          !(isTravelGuideEnabled(data?.country_code) && isTravelGuideView),
        disabled: false,
        icon: <UserIcon />,
        onClick: () => navigate(getFullPath(getRegistrationPath())),
      },
    };
    if (totalIncompletedGuests > 0) {
      links[NavLinks.onlineChekin].extraData = t('sidebar_guests_left', {
        count: totalIncompletedGuests,
        defaultValue: '{{count}} left',
      });
      links[NavLinks.onlineChekin].extraDataVariant = 'warning';
    } else if (isRegistrationComplete) {
      links[NavLinks.onlineChekin].extraData = t('completed');
      links[NavLinks.onlineChekin].extraDataVariant = 'teal';
    }

    if (isRemoteAccessEnabled) {
      links[NavLinks.virtualKeys] = {
        label: NavLinks.virtualKeys,
        selected: Boolean(isRemoteAccessView),
        disabled: false,
        icon: <KeyRoundIcon />,
        onClick: () => navigate(getFullPath(PATHS.remoteAccess)),
      };
    }
    if (isChatEnabled) {
      links[NavLinks.chat] = {
        label: NavLinks.chat,
        selected: Boolean(isChatView),
        disabled: false,
        icon: <MessageCircleIcon />,
        onClick: () => navigate(getFullPath(PATHS.chat)),
      };
    }
    if (hasGuidebooks) {
      links[NavLinks.guidebooks] = {
        label: NavLinks.guidebooks,
        selected: isGuidebooksView,
        disabled: false,
        icon: <BookOpen />,
        onClick: () => navigate(homeGuidebooksPath),
      };
    }
    if (isTravelGuideEnabled(data?.country_code) && data?.upselling?.active) {
      links[NavLinks.travelGuide] = {
        label: NavLinks.travelGuide,
        selected: Boolean(isTravelGuideView),
        disabled: false,
        icon: <Compass />,
        onClick: () => navigate(getFullPath(PATHS.travelGuide)),
      };
    }
    if (arePaymentsAvailable && paymentsCart?.pay_now_payments) {
      links[NavLinks.paymentCart] = {
        label: NavLinks.paymentCart,
        selected: isPaymentsView,
        disabled: false,
        icon: <CreditCard />,
        onClick: () =>
          navigate(getFullPath(getPaymentPath()), {state: {from: currentPage}}),
      };

      const amountToPay = Number(totalToPayNow);
      if (amountToPay > 0) {
        const amountLabel = Number.isInteger(amountToPay)
          ? String(amountToPay)
          : amountToPay.toFixed(2);
        links[NavLinks.paymentCart]['extraData'] = `${currencySymbol}${amountLabel}`;
        links[NavLinks.paymentCart]['extraDataVariant'] = 'warning';
      }
    }
    if (isCheckoutEnabled && isAvailable) {
      links[NavLinks.checkOut] = {
        label: NavLinks.checkOut,
        selected: Boolean(isCheckOutView),
        disabled: false,
        icon: <LogOut />,
        onClick: () => navigate(getFullPath(PATHS.checkout.main)),
      };
    }
    if (hasLuggageBookings) {
      links[NavLinks.luggageStorage] = {
        label: NavLinks.luggageStorage,
        selected: Boolean(isLuggageView),
        disabled: false,
        icon: <Luggage />,
        onClick: () => navigate(getFullPath(PATHS.luggage)),
      };
      if (activeLuggageBookings.length > 0) {
        links[NavLinks.luggageStorage].extraData = String(activeLuggageBookings.length);
      }
    }
    if (hasEsims) {
      links[NavLinks.travelEsim] = {
        label: NavLinks.travelEsim,
        selected: Boolean(isEsimView),
        disabled: false,
        icon: <Smartphone />,
        onClick: () => navigate(getFullPath(PATHS.esim.my)),
      };
    }
    if (data?.upselling?.active) {
      links[NavLinks.enhanceYourStay] = {
        label: NavLinks.enhanceYourStay,
        selected: isRecommendationsView,
        disabled: false,
        icon: <Star />,
        onClick: () => {
          track(POSTHOG_EVENTS.RECOMMENDATIONS_CLICKED);
          navigate(getFullPath(PATHS.recommendations.main));
        },
      };
    }
    return links;
  }, [
    isHomeView,
    isPaymentsView,
    isRemoteAccessView,
    isRemoteAccessEnabled,
    hasGuidebooks,
    isGuidebooksView,
    homeGuidebooksPath,
    totalIncompletedGuests,
    isRegistrationComplete,
    t,
    arePaymentsAvailable,
    paymentsCart?.pay_now_payments,
    totalToPayNow,
    currencySymbol,
    isCheckOutView,
    isCheckoutEnabled,
    isAvailable,
    isRecommendationsView,
    isChatView,
    isChatEnabled,
    isTravelGuideView,
    isLuggageView,
    hasLuggageBookings,
    activeLuggageBookings.length,
    isEsimView,
    hasEsims,
    data?.upselling?.active,
    data?.country_code,
    navigate,
    getFullPath,
    currentPage,
    getPaymentPath,
    getRegistrationPath,
    track,
  ]);

  const navItems = React.useMemo(() => {
    return Object.values(mainLinks);
  }, [mainLinks]);

  return {navItems};
}

export {useSideBarLinks};
